(() => {
  if (customElements.get('smart-product-recommendations')) return;

  const TAG_SEPARATOR = '|||TAG|||';

  class SmartProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.dataset.smartRecommendationsInitialized === 'true') return;
      this.dataset.smartRecommendationsInitialized = 'true';
      this.loadRecommendations();
    }

    async loadRecommendations() {
      const grid = this.querySelector('[data-smart-recommendations-grid]');
      if (!grid) return;

      const limit = Math.max(1, Number.parseInt(this.dataset.limit || '4', 10));
      const productId = String(this.dataset.productId || '');
      const sectionId = String(this.dataset.sectionId || '');
      const useComplementary = this.dataset.useComplementary === 'true';
      const minimumScore = Math.max(0, Number.parseInt(this.dataset.minimumScore || '12', 10));
      const debug = this.dataset.debug === 'true';

      const current = {
        id: productId,
        vendor: this.normalize(this.dataset.currentVendor || ''),
        type: this.normalize(this.dataset.currentType || ''),
        title: String(this.dataset.currentTitle || ''),
        tags: this.tagSet(this.dataset.currentTags || ''),
      };
      current.titleTokens = this.meaningfulTitleTokens(current.title, current.vendor);

      const relatedPromise = this.fetchCandidates(this.dataset.relatedUrl, sectionId, 'related');
      const complementaryPromise = useComplementary
        ? this.fetchCandidates(this.dataset.complementaryUrl, sectionId, 'complementary')
        : Promise.resolve([]);

      const [relatedCandidates, complementaryCandidates] = await Promise.all([
        relatedPromise,
        complementaryPromise,
      ]);

      const collectionCandidates = this.getTemplateCandidates('collection');
      const brandCandidates = this.getTemplateCandidates('brand');

      const records = this.aggregateCandidates([
        ...relatedCandidates,
        ...complementaryCandidates,
        ...collectionCandidates,
        ...brandCandidates,
      ], current);

      const similar = records
        .map((record) => this.scoreSimilar(record, current, minimumScore, productId))
        .filter((record) => record.eligible)
        .sort((a, b) => this.compareScored(a, b));

      const complementary = useComplementary
        ? records
            .filter((record) => record.sources.has('complementary'))
            .map((record) => this.scoreComplementary(record, current, productId))
            .filter((record) => record.eligible)
            .sort((a, b) => this.compareScored(a, b))
        : [];

      const selected = [];
      const used = new Set(productId ? [productId] : []);
      const complementaryTarget = useComplementary ? Math.floor(limit / 2) : 0;
      const similarTarget = limit - complementaryTarget;

      this.takeCandidates(similar, similarTarget, used, selected);
      this.takeCandidates(complementary, complementaryTarget, used, selected);

      // If Search & Discovery has no complementary recommendations, use only
      // genuinely relevant similar products. Never fill empty slots with noise.
      if (selected.length < limit) {
        this.takeCandidates(similar, limit - selected.length, used, selected);
      }

      if (selected.length === 0) return;

      const nodes = selected.slice(0, limit).map((record) => {
        const node = record.node.cloneNode(true);
        node.dataset.recommendationScore = String(record.score);
        node.dataset.recommendationReasons = record.reasons.join(', ');
        return node;
      });

      grid.replaceChildren(...nodes);
      this.hidden = false;

      if (debug) {
        // Diagnostics are intentionally console-only so the storefront stays clean.
        console.table(
          selected.map((record) => ({
            product: record.title,
            score: record.score,
            reasons: record.reasons.join(' | '),
            sources: Array.from(record.sources).join(', '),
          })),
        );
      }
    }

    async fetchCandidates(url, sectionId, intent) {
      if (!url) return [];

      try {
        const response = await fetch(url, {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!response.ok) return [];

        const html = await response.text();
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const responses = Array.from(
          documentFragment.querySelectorAll('[data-smart-recommendations-response]'),
        );
        const matchingResponse = responses.find(
          (element) =>
            element.dataset.sectionId === sectionId && element.dataset.intent === intent,
        );
        if (!matchingResponse) return [];

        return Array.from(matchingResponse.querySelectorAll('[data-smart-candidate]')).map((node) =>
          node.cloneNode(true),
        );
      } catch (error) {
        return [];
      }
    }

    getTemplateCandidates(kind) {
      const template = this.querySelector(`template[data-smart-recommendations-${kind}]`);
      if (!template) return [];
      return Array.from(template.content.querySelectorAll('[data-smart-candidate]')).map((node) =>
        node.cloneNode(true),
      );
    }

    aggregateCandidates(nodes, current) {
      const records = new Map();

      for (const node of nodes) {
        const id = String(node.dataset.productId || '');
        if (!id || id === current.id) continue;

        const sourceKind = String(node.dataset.sourceKind || 'collection');
        const collectionScore = Math.max(
          0,
          Number.parseInt(node.dataset.collectionScore || '0', 10) || 0,
        );

        if (!records.has(id)) {
          const vendor = this.normalize(node.dataset.vendor || '');
          const title = String(node.dataset.title || '');
          records.set(id, {
            id,
            node,
            title,
            vendor,
            type: this.normalize(node.dataset.productType || ''),
            tags: this.tagSet(node.dataset.tags || ''),
            titleTokens: this.meaningfulTitleTokens(title, vendor),
            collectionScore,
            sources: new Set([sourceKind]),
          });
          continue;
        }

        const existing = records.get(id);
        existing.collectionScore = Math.max(existing.collectionScore, collectionScore);
        existing.sources.add(sourceKind);

        // Prefer the API-rendered node when available, because Shopify may add
        // recommendation tracking information to its URL.
        const existingSource = String(existing.node.dataset.sourceKind || '');
        if (
          (sourceKind === 'related' || sourceKind === 'complementary') &&
          existingSource !== 'related' &&
          existingSource !== 'complementary'
        ) {
          existing.node = node;
        }
      }

      return Array.from(records.values());
    }

    scoreSimilar(record, current, minimumScore, productId) {
      const sameVendor = Boolean(current.vendor && record.vendor === current.vendor);
      const sameType = Boolean(current.type && record.type && record.type === current.type);
      const commonTags = this.intersectionSize(current.tags, record.tags);
      const commonTitleTokens = this.intersectionSize(current.titleTokens, record.titleTokens);

      let score = record.collectionScore;
      const reasons = [];

      if (record.collectionScore > 0) reasons.push(`coleção +${record.collectionScore}`);
      if (sameType) {
        score += 12;
        reasons.push('tipo +12');
      }
      if (commonTags > 0) {
        const tagPoints = Math.min(commonTags, 3) * 5;
        score += tagPoints;
        reasons.push(`tags +${tagPoints}`);
      }
      if (commonTitleTokens > 0) {
        const titlePoints = Math.min(commonTitleTokens, 3) * 5;
        score += titlePoints;
        reasons.push(`título +${titlePoints}`);
      }
      if (sameVendor) {
        score += 4;
        reasons.push('marca +4');
      }
      if (record.sources.has('related')) {
        score += 2;
        reasons.push('Shopify related +2');
      }

      // Vendor alone is never enough. A candidate must have at least one strong
      // semantic/taxonomic signal, or belong to a clearly specific collection.
      const strongSignal =
        sameType ||
        commonTags > 0 ||
        commonTitleTokens > 0 ||
        record.collectionScore >= 14;

      return {
        ...record,
        score,
        reasons,
        eligible: strongSignal && score >= minimumScore,
        tieBreaker: this.hashString(`${productId}|${record.id}|similar`),
      };
    }

    scoreComplementary(record, current, productId) {
      const sameVendor = Boolean(current.vendor && record.vendor === current.vendor);
      const sameType = Boolean(current.type && record.type && record.type === current.type);
      const commonTags = this.intersectionSize(current.tags, record.tags);
      const commonTitleTokens = this.intersectionSize(current.titleTokens, record.titleTokens);

      // Complementary results only exist when configured by Shopify/Search & Discovery.
      // We trust that curation, while still using context to order the products.
      let score = 20 + record.collectionScore;
      const reasons = ['complementar configurado +20'];

      if (record.collectionScore > 0) reasons.push(`coleção +${record.collectionScore}`);
      if (commonTags > 0) {
        const tagPoints = Math.min(commonTags, 3) * 4;
        score += tagPoints;
        reasons.push(`tags +${tagPoints}`);
      }
      if (commonTitleTokens > 0) {
        const titlePoints = Math.min(commonTitleTokens, 2) * 2;
        score += titlePoints;
        reasons.push(`título +${titlePoints}`);
      }
      if (sameVendor) {
        score += 2;
        reasons.push('marca +2');
      }
      if (sameType) {
        score -= 4;
        reasons.push('mesmo tipo -4');
      }

      return {
        ...record,
        score,
        reasons,
        eligible: true,
        tieBreaker: this.hashString(`${productId}|${record.id}|complementary`),
      };
    }

    compareScored(a, b) {
      if (b.score !== a.score) return b.score - a.score;
      return a.tieBreaker - b.tieBreaker;
    }

    takeCandidates(candidates, count, used, selected) {
      if (count <= 0) return;

      let added = 0;
      for (const candidate of candidates) {
        if (added >= count) break;
        if (used.has(candidate.id)) continue;
        used.add(candidate.id);
        selected.push(candidate);
        added += 1;
      }
    }

    tagSet(rawTags) {
      return new Set(
        String(rawTags || '')
          .split(TAG_SEPARATOR)
          .map((tag) => this.normalize(tag))
          .filter(Boolean),
      );
    }

    meaningfulTitleTokens(title, normalizedVendor) {
      const stopWords = new Set([
        'a', 'o', 'as', 'os', 'de', 'da', 'do', 'das', 'dos', 'e', 'em', 'no', 'na', 'nos',
        'nas', 'ao', 'aos', 'para', 'por', 'com', 'sem', 'um', 'uma', 'uns', 'umas', 'the',
        'and', 'with', 'for', 'from', 'pack', 'kit', 'un', 'uni', 'unid', 'unidade', 'unidades',
        'ml', 'cl', 'dl', 'l', 'mg', 'g', 'gr', 'kg', 'cm', 'mm', 'x', 'spf', 'fps', 'novo',
        'nova', 'new', 'oferta', 'promocao', 'promocional', 'cor', 'tamanho',
      ]);

      const vendorTokens = new Set(
        String(normalizedVendor || '')
          .split(/[^a-z0-9]+/)
          .filter((token) => token.length >= 3),
      );

      const normalizedTitle = this.normalize(title);
      return new Set(
        normalizedTitle
          .split(/[^a-z0-9]+/)
          .filter((token) => {
            if (!token || token.length < 3) return false;
            if (/^\d+$/.test(token)) return false;
            if (stopWords.has(token)) return false;
            if (vendorTokens.has(token)) return false;
            return true;
          }),
      );
    }

    intersectionSize(a, b) {
      if (!a || !b || a.size === 0 || b.size === 0) return 0;
      let count = 0;
      const [smallest, largest] = a.size <= b.size ? [a, b] : [b, a];
      for (const value of smallest) {
        if (largest.has(value)) count += 1;
      }
      return count;
    }

    normalize(value) {
      return String(value || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim();
    }

    hashString(value) {
      let hash = 2166136261;
      for (let index = 0; index < value.length; index += 1) {
        hash ^= value.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
      }
      return hash >>> 0;
    }
  }

  customElements.define('smart-product-recommendations', SmartProductRecommendations);
})();
